import {
  Room,
  RoomEvent,
  RemoteParticipant,
  RemoteTrack,
  Track,
  type RemoteTrackPublication,
  type RoomOptions,
  ConnectionState,
} from 'livekit-client';
import { EventBus, useGameStore } from '@campus/core';
import { API_BASE } from '../lib/apiBase.js';
import { syncVideoBackground } from './videoBackground.js';
import { onCustomBackgroundChange } from '../lib/videoBackgrounds.js';

/** Message clair pour l'utilisateur quand l'activation micro/caméra échoue (permission refusée, device absent, etc.). */
function mediaErrorMessage(err: Error, device: 'microphone' | 'caméra'): string {
  if (err.name === 'NotAllowedError') {
    return `Permission ${device} refusée - vérifie les réglages de ton navigateur pour ce site`;
  }
  if (err.name === 'NotFoundError') {
    return `Aucun ${device} détecté sur cet appareil`;
  }
  return `Impossible d'activer le ${device} (${err.name})`;
}

/** Attente simple (retry meeting-room, Fix I-3) — pas un remplacement des attentes d'événements LiveKit. */
function delay(ms: number): Promise<void> {
  return new Promise((resolve) => setTimeout(resolve, ms));
}

// Fix I-3 : nombre de tentatives (fetch token + connexion) pour rejoindre une
// room meeting-room dédiée, et délai entre elles. Fixe, pas de backoff
// exponentiel — over-engineering pour ce cas (voir task-20-brief.md).
const MEETING_ROOM_MAX_ATTEMPTS = 2;
const MEETING_ROOM_RETRY_DELAY_MS = 1500;
/** Revérification des souscriptions (filet de sécurité, reconcileSubscriptions). */
const SUBSCRIPTION_RECONCILE_MS = 4000;

/**
 * LiveKitService — Gestion des connexions LiveKit côté client.
 *
 * RESPONSABILITÉS :
 *   1. Connexion / déconnexion room de zone
 *   2. Souscriptions sélectives selon les voisins (proximity audio)
 *   3. Isolation bulles (bubbleId — souscriptions filtrées par bubbleId Colyseus)
 *   4. Broadcast pattern Ding-Dong (connexion à la demande, pas permanente)
 *
 * RÈGLE : Cette classe ne connaît pas React ni Zustand.
 *   Elle communique avec le reste de l'app via EventBus.
 *   Elle est instanciée et pilotée par GameBridge.
 */
export class LiveKitService {
  constructor() {
    // Arrière-plan de la caméra changé dans les Réglages : appliqué en direct.
    const resync = () => { for (const room of [this.zoneRoom, this.meetingRoomRoom]) if (room) void syncVideoBackground(room); };
    useGameStore.subscribe((s) => s.settings.videoBackground, resync);
    onCustomBackgroundChange(resync);
    // Filet de sécurité : un événement LiveKit manqué (TrackPublished arrivé
    // avant que la bulle de l'autre soit connue, renégociation interrompue…)
    // laissait une caméra ou un micro non reçus jusqu'à ressortir de la bulle
    // et y revenir (vu le 2026-11-03). Revérifié régulièrement, sans bruit.
    setInterval(() => this.reconcileSubscriptions(), SUBSCRIPTION_RECONCILE_MS);
  }

  /**
   * Remet les souscriptions dans l'état voulu : room de la salle de réunion =
   * tout recevoir (sauf Occupé/Focus) ; room de zone = règles de bulle, de
   * proximité et de podium (mêmes que updateSubscriptions). Ne touche qu'aux
   * publications dont l'état diffère (aucune renégociation sinon).
   */
  private reconcileSubscriptions(): void {
    const meeting = this.meetingRoomRoom;
    if (this.inMeetingRoom && meeting) {
      meeting.remoteParticipants.forEach((participant) => {
        participant.trackPublications.forEach((pub) => {
          if ((pub.kind === Track.Kind.Audio || pub.kind === Track.Kind.Video) && pub.isSubscribed === this.deafened) {
            pub.setSubscribed(!this.deafened);
          }
        });
      });
      return;
    }
    const zone = this.zoneRoom;
    if (!zone || zone.state !== ConnectionState.Connected) return;
    const podiumSpeakerId = useGameStore.getState().podiumSpeakerId;
    const localIdentity = zone.localParticipant.identity;
    zone.remoteParticipants.forEach((participant) => {
      const userId = participant.identity;
      const shouldSubscribe = podiumSpeakerId && localIdentity !== podiumSpeakerId && userId === podiumSpeakerId && !this.deafened
        ? true
        : this.shouldSubscribeTo(userId, false);
      participant.trackPublications.forEach((pub) => {
        if ((pub.kind === Track.Kind.Audio || pub.kind === Track.Kind.Video) && pub.isSubscribed !== shouldSubscribe) {
          console.log(`[LiveKitService:reconcile] setSubscribed(${shouldSubscribe}) → ${userId} [${pub.kind}]`);
          pub.setSubscribed(shouldSubscribe);
        }
      });
    });
  }

  private zoneRoom: Room | null = null;
  private broadcastRoom: Room | null = null;
  private meetingRoomRoom: Room | null = null;
  /**
   * Identité (mapZoneId) de la room meeting-room dédiée actuellement connectée,
   * null hors réunion. Distinct de `!!meetingRoomRoom` : ce dernier ne teste
   * qu'une présence, pas QUELLE room dédiée est active — insuffisant pour
   * détecter une transition directe entre deux meeting-rooms adjacentes A→B
   * (pas de "" intermédiaire, l'hystérésis serveur n'en insère pas). Mis à
   * jour dans switchToMeetingRoom (succès) et remis à null dans
   * switchBackToZoneRoom / échec de connectToMeetingRoom. Voir task-16-brief.md (bug C-1).
   */
  private currentMeetingMapZoneId: string | null = null;

  // ── Compteur de génération (amendement Task 16 — Critical résiduel + Important) ──
  /**
   * Incrémenté à CHAQUE `bubble:changed` reçu (onBubbleChanged), quel que soit le
   * type de transition. `BUBBLE_HYSTERESIS_MS = 800` (ZoneRoom.ts) est un plancher
   * serveur sur l'ÉMISSION de bubble:changed — pas sur la durée d'un aller-retour
   * réseau client (fetch token + connect LiveKit). Sous réseau dégradé, cet
   * aller-retour peut dépasser 800ms : un nouveau bubble:changed peut alors arriver
   * avant que la transition précédente ait fini de se résoudre. Chaque transition
   * asynchrone capture ce compteur à son démarrage et revérifie juste avant de
   * committer un effet partagé (marquer meetingRoomRoom connecté, republier vers
   * zoneRoom, couper/restaurer mic-caméra-écran) : périmée → abandon silencieux,
   * la transition la plus récente gagne. Complète (ne remplace pas) le garde
   * d'identité `currentMeetingMapZoneId` du fix C-1 — celui-ci protège contre une
   * comparaison d'identité incorrecte, celui-ci protège contre un ORDRE
   * d'achèvement réseau incorrect entre deux transitions concurrentes.
   */
  private transitionGeneration = 0;
  /**
   * Cible meeting-room voulue par le DERNIER bubble:changed reçu — null si la
   * cible est "aucune meeting-room" (zone ouverte ou bulle normale). Distinct de
   * `currentMeetingMapZoneId` (cible RÉELLEMENT connectée) : sert à décider, au
   * moment de committer une sortie de meeting-room, si on doit réellement republier
   * vers zoneRoom (cible = aucune) ou si on est en fait au milieu d'une transition
   * directe vers UNE AUTRE meeting-room (cible ≠ null) — auquel cas republier vers
   * zoneRoom ouvrirait une fenêtre audible par toute la zone ouverte pour rien
   * (finding Important de l'amendement Task 16).
   */
  private latestBubbleMeetingTarget: string | null = null;
  /** Génération propriétaire de `meetingRoomRoom` — permet à connectToMeetingRoom
   *  de distinguer "déjà connecté pour CETTE transition" (no-op légitime) de "room
   *  orpheline d'une génération périmée jamais nettoyée" (à déconnecter avant de
   *  connecter la cible fraîche, plutôt que de no-op à tort dessus). */
  private meetingRoomOwnerGeneration: number | null = null;
  /**
   * true tant que zoneRoomMicWasEnabledBeforeMeeting/Camera/ScreenShare représentent
   * un état PAS ENCORE appliqué à zoneRoom (ni consommé). Le republish vers zoneRoom
   * étant désormais gated (voir switchBackToZoneRoom), zoneRoom peut rester dans son
   * état "coupé pour réunion" alors qu'on enchaîne directement vers une AUTRE
   * meeting-room : dans ce cas, switchToMeetingRoom doit réutiliser ces flags tels
   * quels (état pré-PREMIÈRE-réunion de la chaîne) plutôt que de relire l'état actuel
   * de zoneRoom (qui resterait à tort "coupé"/false). Remis à false uniquement quand
   * les flags sont réellement appliqués à zoneRoom (sortie réelle vers la zone).
   */
  private zoneRoomAvFlagsPending = false;

  // État local des voisins (mis à jour par updateSubscriptions)
  private currentNeighbors = new Set<string>();   // userIds dans le rayon Phaser (player:nearby)
  private proximityUserIds = new Set<string>();   // userIds proches selon Colyseus (proximity:changed)
  private currentBubbleId: string | null = null;  // bubbleId du joueur local

  // ── Bulle meeting-room (room LiveKit dédiée) ──────────────
  /**
   * true tant qu'une réunion meeting-room est active. Bascule intégrale de
   * l'usage AV : plus de publication/souscription vers zoneRoom pendant ce
   * temps (pas de double-publication simultanée) — zoneRoom reste connectée
   * pour Colyseus/mouvement, seul l'usage LiveKit est suspendu.
   * Voir onBubbleChanged / switchToMeetingRoom / switchBackToZoneRoom.
   */
  private inMeetingRoom = false;
  // État mic/caméra/partage d'écran de zoneRoom capturé juste avant la bascule, pour restauration exacte à la sortie.
  private zoneRoomMicWasEnabledBeforeMeeting = false;
  private zoneRoomCameraWasEnabledBeforeMeeting = false;
  private zoneRoomScreenShareWasEnabledBeforeMeeting = false;

  // ── Admin mute lock ────────────────────────────────────────
  /** Timestamp jusqu'auquel le micro est verrouillé par un admin. 0 = pas de lock. */
  private _mutedByAdminUntil = 0;
  private _adminMuteTimeout: ReturnType<typeof setTimeout> | null = null;
  // userId → bubbleId des autres joueurs (reçu de Colyseus via GameBridge)
  private playerBubbleIds = new Map<string, string | null>();

  /**
   * Statut Occupé ou Focus : on n'entend ni ne voit plus personne (zone,
   * bulle, salle de réunion). Seul l'orateur d'un podium reste audible.
   * Voir setDeafened().
   */
  private deafened = false;
  /** Notification « son bloqué » déjà affichée pour le blocage en cours. */
  private audioBlockedWarned = false;

  /** Accès lecture seule à la room LiveKit de zone (utilisé par VideoTiles). */
  getZoneRoom(): Room | null {
    return this.zoneRoom;
  }

  /** Accès lecture seule à la room LiveKit meeting-room dédiée (null hors réunion). */
  getMeetingRoomRoom(): Room | null {
    return this.meetingRoomRoom;
  }

  /**
   * Room qui porte l'AV du joueur local en ce moment : meetingRoomRoom
   * pendant une réunion meeting-room active, zoneRoom sinon. Utilisée en
   * interne par mute/unmute/toggleMic/toggleCamera/toggleScreenShare pour
   * que les contrôles HUD agissent sur la bonne room pendant une réunion.
   */
  private get localAvRoom(): Room | null {
    return this.inMeetingRoom && this.meetingRoomRoom ? this.meetingRoomRoom : this.zoneRoom;
  }

  /** Accès lecture seule équivalent à localAvRoom, pour un futur usage UI (ex: VideoTiles). */
  getActiveAvRoom(): Room | null {
    return this.localAvRoom;
  }

  /**
   * Options de capture construites depuis les préférences périphérique
   * persistées (Réglages) — appliquées à la connexion d'une room qui publie
   * de l'AV locale (zoneRoom, meetingRoomRoom). Un deviceId vide (défaut)
   * omet la clé plutôt que de la passer à '' — laisse livekit-client/le
   * navigateur choisir le périphérique système par défaut.
   */
  private buildRoomOptions(): RoomOptions {
    const { selectedMicId, selectedCameraId, selectedSpeakerId } = useGameStore.getState().settings;
    // adaptiveStream : chacun ne reçoit que la qualité utile à la taille de la
    // vignette (rien pour une vignette cachée) ; dynacast : l'émetteur coupe les
    // qualités que personne ne regarde. Avant, tout le monde recevait toutes les
    // caméras en 720p (> 10 Mbit/s à 7) : en perm à 7 le 30/10, une membre ne
    // voyait plus que le partage d'écran (prioritaire), caméras noires.
    const options: RoomOptions = { adaptiveStream: true, dynacast: true };
    if (selectedMicId) options.audioCaptureDefaults = { deviceId: selectedMicId };
    if (selectedCameraId) options.videoCaptureDefaults = { deviceId: selectedCameraId };
    if (selectedSpeakerId) options.audioOutput = { deviceId: selectedSpeakerId };
    return options;
  }

  /**
   * Applique un changement de périphérique en direct sur les rooms
   * actuellement connectées qui publient de l'AV locale (zoneRoom,
   * meetingRoomRoom — pas broadcastRoom, réception seule). Si aucune room
   * n'est connectée, no-op silencieux : le prochain connect() appliquera la
   * préférence via buildRoomOptions() (déjà persistée par le store côté UI).
   */
  async switchDevice(kind: MediaDeviceKind, deviceId: string): Promise<void> {
    const rooms = [this.zoneRoom, this.meetingRoomRoom].filter((r): r is Room => !!r);
    await Promise.all(rooms.map((room) =>
      room.switchActiveDevice(kind, deviceId).catch((err: unknown) => {
        console.warn(`[LiveKitService] switchActiveDevice(${kind}) échoué:`, err);
      }),
    ));
  }

  /** true si `gen` ne correspond plus au compteur de génération courant — une
   *  transition plus récente a été déclenchée depuis. Voir transitionGeneration. */
  private isStaleGeneration(gen: number): boolean {
    return gen !== this.transitionGeneration;
  }

  // ── Point de passage unique pour les mutations mic/caméra/écran (audit ──
  // fix I-4 élargi, 2026-08-22) ──────────────────────────────────────────
  /**
   * Verrou d'application AV par room (FIFO, pas d'annulation) : sérialise les
   * mutations mic/caméra/écran sur `room.localParticipant`. Si un appel est
   * déjà en vol sur CETTE room, le nouveau ATTEND sa résolution avant de
   * démarrer — jamais deux `set*Enabled` concurrents sur le même
   * localParticipant. Choix explicite : file d'attente (attendre), pas
   * d'annulation — livekit-client n'expose pas d'AbortSignal sur
   * setMicrophoneEnabled/setCameraEnabled/setScreenShareEnabled, donc
   * interrompre un appel déjà en vol n'est pas possible proprement. La
   * revérification de fraîcheur juste après avoir obtenu son tour (et entre
   * chaque set individuel, voir applyAvStateLocked) garantit qu'une
   * transition périmée n'applique rien une fois son tour arrivé, même si son
   * `gen` était encore valide au moment où elle a rejoint la file.
   */
  private readonly avLocks = new WeakMap<Room, Promise<unknown>>();

  /**
   * Primitive de verrouillage partagée — sérialise `fn` contre tout autre
   * appel `withRoomLock`/`applyAvState` en vol sur LA MÊME room (voir le
   * commentaire de `avLocks` pour le rationale FIFO / pas d'annulation).
   * Extrait de `applyAvState` (2026-08-22, fix #11) pour être réutilisé par
   * les toggles HUD (`toggleMic`/`toggleCamera`/`toggleScreenShare`), qui ont
   * besoin de LA MÊME sérialisation mais PAS de la sémantique de péremption
   * par génération d'`applyAvState` : un clic HUD est une intention
   * utilisateur directe, pas une transition automatique déclenchée par
   * bubble:changed — elle ne doit jamais être silencieusement abandonnée au
   * seul motif qu'une transition a démarré entre-temps (ce serait une
   * régression : un clic "couper le micro" qui ne s'applique jamais). Ni la
   * gestion d'erreur spécifique aux toggles (toast mediaErrorMessage) ne
   * passe par applyAvStateLocked, qui avale ses erreurs en interne avec un
   * simple warn générique.
   */
  private async withRoomLock<T>(room: Room, fn: () => Promise<T>): Promise<T> {
    const prior = this.avLocks.get(room) ?? Promise.resolve();
    const run = prior.then(fn, fn);
    // Le verrou stocké ne doit jamais lui-même rejeter (sans quoi le PROCHAIN
    // appelant en file verrait son `.then` sauter droit au handler d'erreur
    // sans réellement attendre celui-ci) — ce `.catch` est une garde
    // défensive sur la valeur STOCKÉE dans la Map ; la valeur RETOURNÉE à cet
    // appelant (`run`) n'est pas affectée et propage toujours le vrai résultat
    // (ou la vraie erreur) de `fn`.
    this.avLocks.set(room, run.catch(() => undefined));
    return run;
  }

  /**
   * Point de passage unique pour toute mutation mic/caméra/écran DÉCLENCHÉE
   * PAR UNE TRANSITION AUTOMATIQUE (ferme les points #1/#2/#4/#5 de l'audit
   * generation-guard : application AV sur meetingRoomRoom en fin de
   * switchToMeetingRoom, coupure de zoneRoom en fin de switchToMeetingRoom,
   * republish de zoneRoom en fin de switchBackToZoneRoom). Revérifie `gen`
   * avant CHAQUE appel `set*Enabled` (pas seulement avant le bloc) via
   * applyAvStateLocked, ET sérialise contre tout autre appel en vol sur la
   * MÊME room via withRoomLock — les deux gardes sont nécessaires : le
   * premier ferme les trous intra-fonction, le second ferme la course
   * inter-fonctions entre switchToMeetingRoom (coupure zoneRoom) et
   * switchBackToZoneRoom (republish zoneRoom), qui peuvent s'exécuter en
   * parallèle sur le même zoneRoom.localParticipant. Pour les clics HUD
   * directs, voir withRoomLock (utilisé nu par les toggles, sans la
   * sémantique de génération).
   *
   * Retourne `true` seulement si les 3 mutations ont pu être tentées
   * jusqu'au bout sans péremption intermédiaire (sauter une mutation parce
   * que localParticipant est déjà dans l'état cible ne compte pas comme un
   * abandon).
   */
  private async applyAvState(
    room: Room,
    gen: number,
    target: { mic: boolean; camera: boolean; screen: boolean },
    label: string,
  ): Promise<boolean> {
    return this.withRoomLock(room, () => this.applyAvStateLocked(room, gen, target, label));
  }

  private async applyAvStateLocked(
    room: Room,
    gen: number,
    target: { mic: boolean; camera: boolean; screen: boolean },
    label: string,
  ): Promise<boolean> {
    if (this.isStaleGeneration(gen)) {
      console.log(`[LiveKitService:av-lock] applyAvState(${label}, gen=${gen}) périmée avant application (courante=${this.transitionGeneration}) — abandon, verrou libéré sans effet`);
      return false;
    }
    const lp = room.localParticipant;
    try {
      if (lp.isMicrophoneEnabled !== target.mic) await lp.setMicrophoneEnabled(target.mic);
      if (this.isStaleGeneration(gen)) {
        console.log(`[LiveKitService:av-lock] applyAvState(${label}, gen=${gen}) périmée après mic (courante=${this.transitionGeneration}) — abandon avant caméra/écran`);
        return false;
      }
      if (lp.isCameraEnabled !== target.camera) await lp.setCameraEnabled(target.camera);
      if (this.isStaleGeneration(gen)) {
        console.log(`[LiveKitService:av-lock] applyAvState(${label}, gen=${gen}) périmée après caméra (courante=${this.transitionGeneration}) — abandon avant écran`);
        return false;
      }
      if (lp.isScreenShareEnabled !== target.screen) await lp.setScreenShareEnabled(target.screen);
      if (this.isStaleGeneration(gen)) {
        console.log(`[LiveKitService:av-lock] applyAvState(${label}, gen=${gen}) périmée après écran (courante=${this.transitionGeneration}) — commit refusé`);
        return false;
      }
    } catch (err) {
      console.warn(`[LiveKitService:av-lock] applyAvState(${label}) échoué:`, err);
      return false;
    }
    return true;
  }

  /**
   * Attend que toute mutation AV en vol sur `room` (via applyAvState) se
   * termine, SANS en démarrer une nouvelle — à utiliser avant toute LECTURE
   * de `room.localParticipant.isMicrophoneEnabled`/etc. qui doit refléter un
   * état stabilisé plutôt qu'une valeur transitoire pas-encore-appliquée.
   *
   * Trouvé pendant le smoke test de l'unification (2026-08-22), hors de la
   * liste d'audit initialement validée : switchBackToZoneRoom lisait
   * `roomToLeave.localParticipant.isMicrophoneEnabled` de façon synchrone,
   * potentiellement PENDANT que switchToMeetingRoom(gen précédent) était
   * encore en train d'appliquer sa propre cible sur cette même room (verrou
   * de applyAvState encore tenu) — la lecture capturait alors la valeur
   * D'AVANT cette application en cours, pas la valeur réellement voulue par
   * l'entrée en réunion. Une entrée puis sortie très rapide pouvait donc
   * faire lire "mic éteint" à la sortie alors que l'entrée était en train de
   * l'allumer, et restaurer zoneRoom dans le mauvais état. Voir
   * switchBackToZoneRoom.
   */
  private async drainAvLock(room: Room | null): Promise<void> {
    if (!room) return;
    await (this.avLocks.get(room) ?? Promise.resolve());
  }

  // Handlers EventBus — stockés pour pouvoir les retirer dans disconnectFromZone.
  private readonly onBubbleChanged = ({ previousBubbleId, previousType, newBubbleId, newType }: { previousBubbleId: string; previousType: string; newBubbleId: string; newType: string }) => {
    // Amendement Task 16 (Critical résiduel + Important) : chaque bubble:changed
    // bump la génération ET fixe la cible meeting-room "officiellement voulue" —
    // AVANT tout branchement, pour que les transitions asynchrones en vol
    // (potentiellement encore basées sur un ancien bubble:changed sous réseau
    // dégradé) puissent se comparer à un état toujours à jour au moment de
    // committer. Voir transitionGeneration/latestBubbleMeetingTarget.
    const generation = ++this.transitionGeneration;
    this.latestBubbleMeetingTarget = (newType === 'meeting-room' && newBubbleId) ? newBubbleId : null;

    // DEBUG — retirer une fois la chaîne bubble:changed confirmée
    console.log(`[LiveKitService:bubble] ✓ bubble:changed reçu prev="${previousBubbleId}"(${previousType}) new="${newBubbleId}"(${newType}) room=${!!this.zoneRoom} gen=${generation}`);

    // Entrée en bulle meeting-room : bascule intégrale de l'usage AV vers une
    // room LiveKit dédiée (voir switchToMeetingRoom pour le détail de l'ordre
    // pré-connexion/coupure — spike : évite le trou audio de 120-160ms d'un
    // disconnect+connect naïf).
    if (newType === 'meeting-room' && newBubbleId) {
      if (this.inMeetingRoom && this.currentMeetingMapZoneId === newBubbleId) {
        // Déjà connecté à CETTE room meeting précise (identité comparée, pas
        // juste présence) — no-op idempotent, ex: bubble:changed redondant.
        return;
      }
      if (this.inMeetingRoom && this.currentMeetingMapZoneId !== null) {
        // Transition DIRECTE entre deux meeting-rooms adjacentes A→B — pas de
        // "" intermédiaire, l'hystérésis serveur n'en insère pas ici.
        // Appeler switchToMeetingRoom(B) directement pendant qu'on est encore
        // connecté à A ferait passer par le garde présence-only de
        // connectToMeetingRoom ("if (this.meetingRoomRoom) return") qui
        // croirait à tort être déjà connecté à B → résolution silencieuse →
        // switchToMeetingRoom écraserait les flags de restauration avec
        // l'état résiduel de A (bug C-1, voir task-16-brief.md).
        // Fix : traiter la transition comme une sortie complète de A
        // (restauration + déconnexion propre + recalcul des flags) suivie
        // immédiatement d'une entrée propre dans B — jamais une fusion des
        // deux qui mélangerait les états de A et B. Le republish vers zoneRoom
        // à l'intérieur de switchBackToZoneRoom est gated (voir plus bas) :
        // latestBubbleMeetingTarget vaut B ici, donc ce republish sera annulé.
        console.log(`[LiveKitService:meeting] transition directe meeting-room ${this.currentMeetingMapZoneId} → ${newBubbleId} (gen=${generation})`);
        void (async () => {
          await this.switchBackToZoneRoom(null, generation);
          await this.switchToMeetingRoom(newBubbleId, generation);
        })();
        return;
      }
      void this.switchToMeetingRoom(newBubbleId, generation);
      return;
    }

    // Sortie de bulle meeting-room : ordre inverse — zoneRoom d'abord, puis
    // déconnexion de la room dédiée (voir switchBackToZoneRoom).
    if (previousType === 'meeting-room' && newType !== 'meeting-room') {
      void this.switchBackToZoneRoom(newBubbleId || null, generation);
      return;
    }

    // Autres transitions (bulle normale ↔ hors bulle) : comportement inchangé.
    // Recalculer les souscriptions LiveKit avec le nouveau bubbleId du joueur local.
    // Never trust the client : cette valeur vient du serveur Colyseus via ZoneRoom.handleMove().
    // Si newBubbleId !== "" → on entre en bulle fixe, shouldSubscribeTo ignorera la proximité.
    // Si newBubbleId === "" → on sort de bulle fixe, la proximité redevient active.
    this.updateSubscriptions(this.currentNeighbors, newBubbleId || null, this.playerBubbleIds);
  };

  /**
   * Bascule intégrale de l'usage AV zoneRoom → room meeting-room dédiée.
   *
   * Ordre (mesures du spike de faisabilité, cf. task-14-brief.md) :
   *   1. Fetch du token (nouvel appel API — mapZoneId dérive la room dédiée).
   *   2. Pré-connexion à la room dédiée AVANT de toucher à zoneRoom : un
   *      disconnect()-puis-connect() naïf produit un trou de ~120-160ms
   *      audio/vidéo ; pré-connecter avant de couper produit un chevauchement
   *      (trou négatif, imperceptible).
   *   3. Une fois connecté, coupe publication + souscription AV vers
   *      zoneRoom pour ce participant. zoneRoom reste connectée (Colyseus/
   *      mouvement continue) — seul l'usage LiveKit est suspendu.
   */
  private async switchToMeetingRoom(mapZoneId: string, gen: number): Promise<void> {
    const jwt = useGameStore.getState().jwt;
    const currentZoneId = useGameStore.getState().currentZoneId; // "{tenantId}:{zoneSlug}"
    const colonIdx = currentZoneId ? currentZoneId.indexOf(':') : -1;
    if (!jwt || !currentZoneId || colonIdx === -1) {
      console.warn('[LiveKitService:meeting] jwt/currentZoneId absent — connexion meeting-room annulée');
      EventBus.emit('ui:toast', { message: "Impossible de rejoindre l'audio/vidéo de la réunion - réessaie dans quelques secondes.", tone: 'danger' });
      return;
    }
    const tenantId = currentZoneId.slice(0, colonIdx);
    const zoneSlug = currentZoneId.slice(colonIdx + 1);

    // Fix I-3 (task-20) : un échec de fetch token ou de connexion à la room
    // dédiée ne doit JAMAIS rester silencieux — sans signal, le participant
    // reste sur zoneRoom (AV potentiellement déjà coupée par la suite de cette
    // méthode) sans jamais rejoindre la réunion, et sans savoir pourquoi. On
    // retente MEETING_ROOM_MAX_ATTEMPTS fois au total (fixe, pas de backoff
    // exponentiel), en repartant à CHAQUE tentative du fetch token (jamais
    // juste connectToMeetingRoom avec un token de la tentative précédente,
    // potentiellement expiré/invalide entre-temps).
    let token: string | undefined;
    let url: string | undefined;

    for (let attempt = 1; attempt <= MEETING_ROOM_MAX_ATTEMPTS; attempt++) {
      const isLastAttempt = attempt === MEETING_ROOM_MAX_ATTEMPTS;

      try {
        const res = await fetch(
          `${API_BASE}/media/livekit-token?tenantId=${encodeURIComponent(tenantId)}&zoneSlug=${encodeURIComponent(zoneSlug)}&mapZoneId=${encodeURIComponent(mapZoneId)}`,
          { headers: { Authorization: `Bearer ${jwt}`, 'X-Tenant-ID': tenantId } },
        );
        if (!res.ok) throw new Error(`HTTP ${res.status}`);
        ({ token, url } = await res.json() as { token: string; url: string });
      } catch (err) {
        // Fix I-3 (review) : vérifier la fraîcheur AVANT tout toast, pas seulement
        // avant de retenter. Sans ce garde, un fetch en vol au moment où un
        // bubble:changed plus récent bump la génération (transition suivante déjà
        // réussie ailleurs, ex: l'utilisateur a rejoint une autre réunion entre-
        // temps) déclencherait quand même un toast d'échec — faux signal pour une
        // transition que l'utilisateur a lui-même abandonnée. Abandon totalement
        // silencieux dans ce cas (pas de toast, pas de console.warn), cohérent avec
        // les autres abandons de fraîcheur de cette méthode (voir plus bas).
        if (this.isStaleGeneration(gen)) {
          console.log(`[LiveKitService:meeting] switchToMeetingRoom(${mapZoneId}, gen=${gen}) périmée pendant le fetch token (tentative ${attempt}/${MEETING_ROOM_MAX_ATTEMPTS}, courante=${this.transitionGeneration}) — abandon silencieux, aucun toast`);
          return;
        }
        console.warn(`[LiveKitService:meeting] fetch token meeting-room échoué (tentative ${attempt}/${MEETING_ROOM_MAX_ATTEMPTS}):`, err);
        if (isLastAttempt) {
          EventBus.emit('ui:toast', { message: 'Connexion à la réunion impossible - contacte un administrateur si ça persiste.', tone: 'danger' });
          return;
        }
        EventBus.emit('ui:toast', { message: "Impossible de rejoindre l'audio/vidéo de la réunion - réessaie dans quelques secondes.", tone: 'danger' });
        await delay(MEETING_ROOM_RETRY_DELAY_MS);
        // Périmée pendant l'attente : une transition plus récente a pris le relais
        // entre-temps, inutile (et potentiellement nuisible) de retenter.
        if (this.isStaleGeneration(gen)) return;
        continue;
      }

      // Amendement Task 16 : vérifier la fraîcheur juste après l'aller-retour réseau
      // du fetch token, AVANT de toucher à quoi que ce soit — si un bubble:changed
      // plus récent est arrivé pendant ce fetch, abandon silencieux, aucun état
      // partagé touché (ni currentMeetingMapZoneId, ni les flags AV).
      if (this.isStaleGeneration(gen)) {
        console.log(`[LiveKitService:meeting] switchToMeetingRoom(${mapZoneId}, gen=${gen}) périmée après fetch token (courante=${this.transitionGeneration}) — abandon avant connexion`);
        return;
      }

      try {
        await this.connectToMeetingRoom(token, url, gen);
        break; // Succès — sort de la boucle de retry, poursuit la suite de la méthode.
      } catch (err) {
        // Fix I-3 (review) : même garde de fraîcheur qu'au catch du fetch token
        // ci-dessus, AVANT tout toast — voir commentaire détaillé là-bas. Ce garde
        // couvre aussi le reset de currentMeetingMapZoneId : s'il est périmé ici,
        // une transition plus récente possède déjà l'état partagé, on n'y touche
        // pas (ne pas écraser un currentMeetingMapZoneId déjà valide).
        if (this.isStaleGeneration(gen)) {
          console.log(`[LiveKitService:meeting] switchToMeetingRoom(${mapZoneId}, gen=${gen}) périmée pendant connectToMeetingRoom (tentative ${attempt}/${MEETING_ROOM_MAX_ATTEMPTS}, courante=${this.transitionGeneration}) — abandon silencieux, aucun toast`);
          return;
        }
        console.warn(`[LiveKitService:meeting] connexion room meeting-room échouée (tentative ${attempt}/${MEETING_ROOM_MAX_ATTEMPTS}):`, err);
        if (isLastAttempt) {
          // Sans ce reset, un mapZoneId resterait mémorisé pour une room jamais
          // rejointe : le garde identity-aware ci-dessus (onBubbleChanged)
          // pourrait à tort croire qu'on est déjà connecté à cette room au
          // prochain bubble:changed vers le même mapZoneId. Fraîcheur déjà
          // confirmée par le garde ci-dessus, pas besoin de la revérifier.
          this.currentMeetingMapZoneId = null;
          EventBus.emit('ui:toast', { message: 'Connexion à la réunion impossible - contacte un administrateur si ça persiste.', tone: 'danger' });
          return;
        }
        EventBus.emit('ui:toast', { message: "Impossible de rejoindre l'audio/vidéo de la réunion - réessaie dans quelques secondes.", tone: 'danger' });
        await delay(MEETING_ROOM_RETRY_DELAY_MS);
        if (this.isStaleGeneration(gen)) return;
        continue;
      }
    }

    if (!token || !url) {
      // Ne devrait jamais arriver (la boucle ci-dessus return sur tout échec
      // à la dernière tentative) — garde défensive pour satisfaire le typage.
      return;
    }

    // Attendre qu'une éventuelle application AV encore en vol sur zoneRoom (un
    // switchBackToZoneRoom d'une transition antérieure pas encore terminé
    // d'appliquer son republish) se termine AVANT de lire son état — même
    // risque symétrique que dans switchBackToZoneRoom, voir drainAvLock.
    await this.drainAvLock(this.zoneRoom);

    // Capturer l'état AV à reproduire sur la room dédiée (continuité perçue) et à
    // restaurer exactement à la sortie. zoneRoomAvFlagsPending distingue deux cas :
    //   - false (cas normal) : on entre en réunion depuis la zone ouverte, l'état
    //     RÉEL de zoneRoom fait foi.
    //   - true : on enchaîne directement depuis UNE AUTRE meeting-room (switchBack-
    //     ToZoneRoom vient de gater son republish vers zoneRoom, voir plus bas) —
    //     zoneRoom est resté coupé (état résiduel de la réunion précédente), donc
    //     relire son état live donnerait à tort "tout coupé". On réutilise alors
    //     les flags tels quels (état pré-PREMIÈRE-réunion de la chaîne), portés
    //     sans interruption d'une meeting-room à l'autre jusqu'à la vraie sortie.
    const zoneLp = this.zoneRoom?.localParticipant;
    const micWasEnabled = this.zoneRoomAvFlagsPending
      ? this.zoneRoomMicWasEnabledBeforeMeeting
      : (zoneLp?.isMicrophoneEnabled ?? false);
    const cameraWasEnabled = this.zoneRoomAvFlagsPending
      ? this.zoneRoomCameraWasEnabledBeforeMeeting
      : (zoneLp?.isCameraEnabled ?? false);
    const screenShareWasEnabled = this.zoneRoomAvFlagsPending
      ? this.zoneRoomScreenShareWasEnabledBeforeMeeting
      : (zoneLp?.isScreenShareEnabled ?? false);

    // 1. Room dédiée déjà connectée par la boucle de retry ci-dessus, AVANT de
    //    toucher à zoneRoom (pré-connexion, cf. doc de méthode plus haut).

    // connectToMeetingRoom peut avoir résolu SANS exception tout en ayant lui-même
    // abandonné en interne (périmée après connexion, ou slot déjà pris par une
    // génération plus récente) — ne committer que si CETTE génération a bien
    // remporté le slot meetingRoomRoom.
    if (this.isStaleGeneration(gen) || this.meetingRoomOwnerGeneration !== gen) {
      console.log(`[LiveKitService:meeting] switchToMeetingRoom(${mapZoneId}, gen=${gen}) périmée après connexion — abandon du commit (propriétaire actuel=${this.meetingRoomOwnerGeneration})`);
      return;
    }

    this.inMeetingRoom = true;
    // localAvRoom résout désormais vers meetingRoomRoom — signal pour
    // VideoTiles (et tout futur consommateur de getActiveAvRoom()), sans
    // quoi le swap de room est invisible côté React (trouvé en review,
    // 2026-08-23 : plus aucune vidéo affichée pendant une réunion).
    EventBus.emit('livekit:av-room-changed', {});
    this.currentMeetingMapZoneId = mapZoneId;
    this.zoneRoomMicWasEnabledBeforeMeeting = micWasEnabled;
    this.zoneRoomCameraWasEnabledBeforeMeeting = cameraWasEnabled;
    this.zoneRoomScreenShareWasEnabledBeforeMeeting = screenShareWasEnabled;
    this.zoneRoomAvFlagsPending = true;

    // 2. Reproduire l'état AV sur la room dédiée — via le point de passage
    //    unique (garde de fraîcheur par étape + verrou par room, voir
    //    applyAvState). Le résultat n'est pas vérifié ici : si périmé en
    //    cours de route, applyAvState a déjà loggé et n'a rien appliqué au-
    //    delà de son dernier checkpoint franchi — rien de plus à faire côté
    //    appelant (pas de flags à annuler pour CETTE room, elle n'est pas
    //    republiée ailleurs).
    if (this.meetingRoomRoom) {
      await this.applyAvState(
        this.meetingRoomRoom,
        gen,
        { mic: micWasEnabled, camera: cameraWasEnabled, screen: screenShareWasEnabled },
        `switchToMeetingRoom(${mapZoneId})→meetingRoomRoom`,
      );
    }

    // 3. Couper publication + souscription AV vers zoneRoom.
    if (this.zoneRoom) {
      const cutApplied = await this.applyAvState(
        this.zoneRoom,
        gen,
        { mic: false, camera: false, screen: false },
        `switchToMeetingRoom(${mapZoneId})→zoneRoom cut`,
      );
      if (cutApplied) {
        // updateSubscriptions no-op ses souscriptions tant que inMeetingRoom === true (voir garde).
        this.updateSubscriptions(this.currentNeighbors, this.currentBubbleId, this.playerBubbleIds);
      }
    }
  }

  /**
   * Sortie de meeting-room : rétablit d'abord publication/souscription
   * zoneRoom, PUIS coupe la room dédiée — ordre inverse de l'entrée, pour
   * garder le même principe de chevauchement (jamais de trou audio total).
   *
   * Amendement Task 16 : le republish vers zoneRoom est désormais GATED (finding
   * Important) — voir shouldRepublishToZone plus bas. La déconnexion de la room
   * dédiée quittée (roomToLeave, capturée par référence AVANT tout await) a lieu
   * inconditionnellement (c'est notre responsabilité de la quitter dans tous les
   * cas), mais de façon compare-and-swap sur `this.meetingRoomRoom` pour ne
   * jamais écraser une room qu'une transition plus récente aurait déjà connectée
   * entre-temps.
   */
  private async switchBackToZoneRoom(newBubbleId: string | null, gen: number): Promise<void> {
    // Lignes synchrones — s'exécutent dans le même tick que l'appel (aucun await
    // avant), donc toujours fraîches par construction : jamais de staleness
    // possible ici, cf. onBubbleChanged qui vient de bumper `gen`.
    this.inMeetingRoom = false;
    // localAvRoom résout de nouveau vers zoneRoom — voir le commentaire
    // équivalent dans switchToMeetingRoom.
    EventBus.emit('livekit:av-room-changed', {});
    this.currentMeetingMapZoneId = null;
    const roomToLeave = this.meetingRoomRoom;

    // Attendre qu'une éventuelle application AV encore en vol sur roomToLeave
    // (switchToMeetingRoom(gen précédent) qui n'aurait pas fini d'appliquer sa
    // cible sur cette même room, verrou applyAvState encore tenu) se termine
    // AVANT de lire son état — sans quoi la lecture ci-dessous capture une
    // valeur transitoire pas-encore-appliquée plutôt que l'état réellement
    // voulu par l'entrée en réunion (trouvé au smoke test, voir drainAvLock).
    await this.drainAvLock(roomToLeave);

    // Mic/caméra/partage d'écran : on lit l'état RÉEL de roomToLeave (la room
    // qu'on quitte, capturée ci-dessus), PAS zoneRoomMicWasEnabledBeforeMeeting/
    // ...CameraWasEnabledBeforeMeeting/...ScreenShareWasEnabledBeforeMeeting (état
    // capturé à l'ENTRÉE). Les contrôles HUD (toggleMic/toggleCamera/
    // toggleScreenShare) ciblent tous meetingRoomRoom pendant la réunion — via
    // localAvRoom —, donc si le participant a coupé son micro/caméra ou
    // démarré/arrêté son partage en cours de réunion, c'est cet état-là qui doit
    // décider pour les 3 flux, de façon identique. Sinon on rallumerait de force
    // sur zoneRoom un micro/caméra explicitement coupé pendant la réunion — micro
    // chaud suite à une action de confidentialité délibérée (Fix I-1, task-18).
    const micActiveAtExit = roomToLeave?.localParticipant.isMicrophoneEnabled ?? false;
    const cameraActiveAtExit = roomToLeave?.localParticipant.isCameraEnabled ?? false;
    const screenShareActiveAtExit = roomToLeave?.localParticipant.isScreenShareEnabled ?? false;

    // Gating du republish vers zoneRoom (finding Important, amendement Task 16) :
    // ne republier QUE si (a) aucun bubble:changed plus récent n'a supplanté cette
    // transition, ET (b) la cible réellement voulue par le DERNIER bubble:changed
    // est bien "aucune meeting-room". (b) n'est PAS "meetingRoomRoom pas encore
    // connecté à la suivante" — ce test-là serait faux pendant tout l'aller-retour
    // réseau de connexion à B et republierait quand même. Sans ce gating, une
    // transition A→B directe (ou une A→B rapide sous réseau dégradé) republie
    // brièvement vers zoneRoom — audible par toute la zone ouverte — avant d'être
    // re-coupée par switchToMeetingRoom(B) juste après : un vrai trou d'isolation,
    // pas qu'un délai perceptible.
    const shouldRepublishToZone = !this.isStaleGeneration(gen) && this.latestBubbleMeetingTarget === null;

    if (this.zoneRoom && shouldRepublishToZone) {
      // roomToLeave === null a DEUX causes distinctes, à ne pas confondre
      // (fix I-1 résiduel, audit generation-guard #5) :
      //   (a) zoneRoom n'a JAMAIS été coupée pour cette réunion — connectToMeetingRoom
      //       n'a jamais abouti avant que le joueur ne ressorte de la bulle serveur
      //       (échec réseau épuisant ses tentatives, etc.). zoneRoomAvFlagsPending
      //       vaut false dans ce cas : aucune coupure n'a eu lieu, donc rien à
      //       restaurer — republier forcerait un état arbitraire (potentiellement
      //       "tout coupé", cf. le bug d'origine) sur un zoneRoom dont l'état AV réel
      //       n'a jamais bougé. No-op sûr, on ne touche pas à l'état AV réel.
      //   (b) zoneRoom A bien été coupée (switchToMeetingRoom est allé jusqu'au bout
      //       de son étape 3) mais meetingRoomRoom a été nettoyé entre-temps par un
      //       mécanisme concurrent (ex: connectToMeetingRoom a détecté une room
      //       orpheline et l'a déconnectée) avant que ce switchBackToZoneRoom n'ait pu
      //       capturer roomToLeave. zoneRoomAvFlagsPending vaut true dans ce cas — ce
      //       sont zoneRoomMicWasEnabledBeforeMeeting/... (l'état RÉEL pré-réunion,
      //       capturé à l'entrée) qui font foi, PAS un état "à la sortie" qu'on ne
      //       peut plus lire (la room a disparu). Restaurer depuis ces flags, pas
      //       depuis un `false` arbitraire.
      const target = roomToLeave
        ? { mic: micActiveAtExit, camera: cameraActiveAtExit, screen: screenShareActiveAtExit }
        : this.zoneRoomAvFlagsPending
          ? {
              mic: this.zoneRoomMicWasEnabledBeforeMeeting,
              camera: this.zoneRoomCameraWasEnabledBeforeMeeting,
              screen: this.zoneRoomScreenShareWasEnabledBeforeMeeting,
            }
          : null;

      if (target === null) {
        console.log(`[LiveKitService:meeting] switchBackToZoneRoom(gen=${gen}) — roomToLeave null et aucun flag en attente (zoneRoom jamais coupée pour cette réunion) — no-op sûr, état AV réel de zoneRoom laissé intact`);
      } else {
        const label = roomToLeave
          ? `switchBackToZoneRoom(gen=${gen})→zoneRoom republish (état réel à la sortie de meetingRoomRoom)`
          : `switchBackToZoneRoom(gen=${gen})→zoneRoom republish (repli flags pré-réunion, roomToLeave déjà nettoyée ailleurs)`;
        const applied = await this.applyAvState(this.zoneRoom, gen, target, label);
        if (applied) {
          this.updateSubscriptions(this.currentNeighbors, newBubbleId, this.playerBubbleIds);
          // Flags réellement appliqués à zoneRoom : plus rien en attente.
          this.zoneRoomMicWasEnabledBeforeMeeting = false;
          this.zoneRoomCameraWasEnabledBeforeMeeting = false;
          this.zoneRoomScreenShareWasEnabledBeforeMeeting = false;
          this.zoneRoomAvFlagsPending = false;
        } else {
          console.log(`[LiveKitService:meeting] switchBackToZoneRoom(gen=${gen}) — republish annulé par applyAvState (périmé en cours d'application) — flags laissés en attente`);
        }
      }
    } else if (this.zoneRoom) {
      // NE PAS réinitialiser les flags ni zoneRoomAvFlagsPending ici : ils restent
      // "en attente" pour que le switchToMeetingRoom qui suit immédiatement (chaîne
      // A→B) — ou celui déclenché par la transition plus récente qui a supplanté
      // celle-ci — les réutilise correctement, plutôt que de relire zoneRoom (dont
      // l'état AV réel est toujours celui, coupé, de la réunion qu'on vient de
      // quitter, pas l'état pré-réunion voulu).
      console.log(`[LiveKitService:meeting] republish vers zoneRoom annulé (gen=${gen} périmée=${this.isStaleGeneration(gen)} cibleMeeting=${this.latestBubbleMeetingTarget ?? 'aucune'})`);
    }

    if (roomToLeave) {
      await roomToLeave.disconnect().catch((err: unknown) => {
        console.warn('[LiveKitService:meeting] déconnexion meetingRoomRoom échouée:', err);
      });
      // Compare-and-swap : ne nettoyer this.meetingRoomRoom que s'il pointe TOUJOURS
      // vers roomToLeave — si une transition plus récente a déjà connecté une
      // nouvelle room entre-temps, ne surtout pas l'écraser.
      if (this.meetingRoomRoom === roomToLeave) {
        this.meetingRoomRoom = null;
        this.meetingRoomOwnerGeneration = null;
      }
    }
  }

  private readonly onProximityChanged = ({ nearbyUserIds }: { nearbyUserIds: string[] }) => {
    console.log('[LiveKitService:proximity] ✓ proximity:changed reçu', nearbyUserIds);
    this.proximityUserIds = new Set(nearbyUserIds);
    // Recalculer les souscriptions avec la nouvelle liste de proximité Colyseus.
    // shouldSubscribeTo fait l'union de currentNeighbors et proximityUserIds.
    this.updateSubscriptions(this.currentNeighbors, this.currentBubbleId, this.playerBubbleIds);
  };

  // ── Connexion à une zone ──────────────────────────────────

  /**
   * Connexion à la room LiveKit de la zone.
   * Appelé dans la séquence de transition APRÈS le switch Phaser.
   * room.connect() attend RoomEvent.Connected — pas de setTimeout hardcodé.
   * RÈGLE : autoSubscribe: false — souscriptions manuelles uniquement.
   */
  async connectToZone(token: string, url: string): Promise<void> {
    await this.disconnectFromZone();

    this.zoneRoom = new Room(this.buildRoomOptions());
    this.setupZoneRoomListeners(this.zoneRoom);

    // Écouter les changements de bulle/proximité AVANT connect() — pas après.
    // Colyseus peut émettre proximity:changed/bubble:changed pendant que
    // zoneRoom.connect() est encore en vol (ex: on rejoint une zone où
    // quelqu'un est déjà présent) ; ces events ne sont envoyés que sur
    // changement (pas rediffusés périodiquement), donc un event manqué ici
    // laisse l'abonnement audio/vidéo bloqué à "non souscrit" jusqu'au
    // prochain changement de proximité — potentiellement jamais dans une
    // conversation statique. updateSubscriptions() no-op tant que
    // this.zoneRoom n'est pas encore assigné (garde déjà en place), donc
    // s'abonner tôt est sans risque.
    EventBus.on('bubble:changed', this.onBubbleChanged);
    EventBus.on('proximity:changed', this.onProximityChanged);

    await this.zoneRoom.connect(url, token, {
      autoSubscribe: false, // ← CRITIQUE : désactive la souscription automatique
    });

    // Publier le track audio local (muté au départ — unmute après transition:complete)
    await this.zoneRoom.localParticipant.setMicrophoneEnabled(true);
    await this.zoneRoom.localParticipant.setMicrophoneEnabled(false);

    // Lecture du son bloquée par le navigateur (pas encore d'interaction, ou
    // blocage revenu plus tard) : relancée à chaque clic ou touche, pour toutes
    // les rooms. Avant : au premier clic seulement, et jamais pour la salle de réunion.
    document.addEventListener('pointerdown', this.unlockAudio, true);
    document.addEventListener('keydown', this.unlockAudio, true);
  }

  /** Relance la lecture du son des rooms où le navigateur l'a bloquée. */
  private readonly unlockAudio = (): void => {
    for (const room of [this.zoneRoom, this.meetingRoomRoom, this.broadcastRoom]) {
      if (room && !room.canPlaybackAudio) void room.startAudio();
    }
  };

  /** Son bloqué par le navigateur : prévenir une fois par blocage (la relance se fait au prochain clic). */
  private onAudioPlaybackChanged(room: Room): void {
    if (room.canPlaybackAudio) { this.audioBlockedWarned = false; return; }
    if (this.audioBlockedWarned) return;
    this.audioBlockedWarned = true;
    EventBus.emit('ui:toast', { message: 'Ton navigateur a bloqué le son. Clique n’importe où pour l’activer.', tone: 'warning' });
  }

  async disconnectFromZone(): Promise<void> {
    EventBus.off('bubble:changed', this.onBubbleChanged);
    EventBus.off('proximity:changed', this.onProximityChanged);
    this.proximityUserIds.clear();

    // Bump de génération inconditionnel : une transition meeting-room encore en
    // vol (fetch token / connect en cours) au moment de ce disconnectFromZone doit
    // être traitée comme périmée à son prochain checkpoint — elle ne doit commiter
    // aucun effet dans une zone qu'on est en train de quitter (voir amendement
    // Task 16, transitionGeneration).
    this.transitionGeneration++;
    document.removeEventListener('pointerdown', this.unlockAudio, true);
    document.removeEventListener('keydown', this.unlockAudio, true);

    if (this.zoneRoom) {
      await this.zoneRoom.disconnect();
      this.zoneRoom = null;
    }
    this.currentNeighbors.clear();

    // Filet de sécurité : si on quitte la zone en pleine réunion meeting-room
    // (edge case — transition de zone pendant une réunion), ne pas laisser
    // meetingRoomRoom en connexion zombie dans la zone suivante.
    if (this.inMeetingRoom || this.meetingRoomRoom) {
      this.inMeetingRoom = false;
      EventBus.emit('livekit:av-room-changed', {});
      this.currentMeetingMapZoneId = null;
      this.zoneRoomMicWasEnabledBeforeMeeting = false;
      this.zoneRoomCameraWasEnabledBeforeMeeting = false;
      this.zoneRoomScreenShareWasEnabledBeforeMeeting = false;
      this.zoneRoomAvFlagsPending = false;
      this.latestBubbleMeetingTarget = null;
      await this.disconnectFromMeetingRoom();
    }
  }

  /**
   * Met à jour les souscriptions selon les voisins détectés par Phaser.
   * Appelé par GameBridge quand EventBus émet player:nearby / player:left-proximity.
   *
   * @param neighborUserIds - Set des userIds dans le rayon de proximité
   * @param localBubbleId   - bubbleId du joueur local (null = pas en bulle)
   * @param allBubbleIds    - Map userId → bubbleId pour tous les joueurs de la zone
   */
  updateSubscriptions(
    neighborUserIds: Set<string>,
    localBubbleId: string | null,
    allBubbleIds: Map<string, string | null>,
  ): void {
    if (!this.zoneRoom) return;

    this.currentNeighbors = neighborUserIds;
    this.currentBubbleId = localBubbleId;
    this.playerBubbleIds = allBubbleIds;

    if (this.inMeetingRoom) {
      // Bascule intégrale vers la room meeting-room dédiée (switchToMeetingRoom) :
      // zoneRoom ne doit plus rien recevoir pendant la réunion, quel que soit
      // l'appelant (proximity:changed, player:nearby...). Idempotent : ne
      // touche que les souscriptions encore actives.
      this.zoneRoom.remoteParticipants.forEach((participant) => {
        participant.trackPublications.forEach((pub) => {
          if ((pub.kind === Track.Kind.Audio || pub.kind === Track.Kind.Video) && pub.isSubscribed) {
            pub.setSubscribed(false);
          }
        });
      });
      return;
    }

    console.log(
      `[LiveKitService:sub] updateSubscriptions — room=${!!this.zoneRoom}`,
      `participants=${this.zoneRoom.remoteParticipants.size}`,
      `localBubble=${localBubbleId ?? 'none'}`,
      `neighbors=[${[...neighborUserIds].join(',')}]`,
      `proximity=[${[...this.proximityUserIds].join(',')}]`,
      `bubbleIds={${[...allBubbleIds.entries()].map(([k, v]) => `${k}:${v ?? 'none'}`).join(',')}}`,
    );

    // ── Règle prioritaire : podium speaker ────────────────────
    // Si un podium est actif, TOUS les participants souscrivent au speaker
    // (audio + vidéo), indépendamment de la proximité ou des bulles.
    // Le speaker lui-même ne reçoit les tracks de personne via cette règle.
    const podiumSpeakerId = useGameStore.getState().podiumSpeakerId;
    const localIdentity = this.zoneRoom.localParticipant.identity;
    const iAmSpeaker = podiumSpeakerId !== null && localIdentity === podiumSpeakerId;

    if (podiumSpeakerId) {
      console.log(`[LiveKitService:sub] podium actif — speaker="${podiumSpeakerId}" iAmSpeaker=${iAmSpeaker}`);
    }

    this.zoneRoom.remoteParticipants.forEach((participant) => {
      const userId = participant.identity;
      let shouldSubscribe: boolean;

      if (podiumSpeakerId) {
        if (iAmSpeaker) {
          // Le speaker ne souscrit à personne via la règle podium
          // mais garde la logique bulle/proximité normale
          shouldSubscribe = this.shouldSubscribeTo(userId);
        } else if (userId === podiumSpeakerId) {
          // Tous les autres souscrivent au speaker
          shouldSubscribe = true;
        } else {
          // Pour les non-speakers entre eux, logique normale
          shouldSubscribe = this.shouldSubscribeTo(userId);
        }
      } else {
        shouldSubscribe = this.shouldSubscribeTo(userId);
      }

      participant.trackPublications.forEach((pub) => {
        if (pub.kind === Track.Kind.Audio || pub.kind === Track.Kind.Video) {
          console.log('[LiveKitService:sub] participant', participant.identity,
            'kind:', pub.kind,
            'shouldSubscribe:', shouldSubscribe, 'isSubscribed:', pub.isSubscribed,
            'trackName:', pub.trackName, 'source:', pub.source);
          // Guard d'idempotence : n'appeler setSubscribed que si l'état change.
          // Appeler setSubscribed(false) puis setSubscribed(true) sur la même pub
          // en rafale provoque une renégociation WebRTC → "createOffer with closed
          // peer connection" → cascade de reconnexion interne livekit-client.
          if (pub.isSubscribed !== shouldSubscribe) {
            console.log(`[LiveKitService:sub] ✓ setSubscribed(${shouldSubscribe}) → ${userId} [${pub.kind}]`);
            pub.setSubscribed(shouldSubscribe);
          }
        }
      });
    });
  }

  /**
   * Active le micro local (unmute).
   * Appelé en fin de séquence de transition zone.
   */
  async unmute(): Promise<void> {
    await this.localAvRoom?.localParticipant.setMicrophoneEnabled(true);
  }

  /**
   * Coupe le micro local (mute).
   * Appelé AU DÉBUT de la séquence de transition zone — avant tout le reste.
   */
  async mute(): Promise<void> {
    await this.localAvRoom?.localParticipant.setMicrophoneEnabled(false);
  }

  /**
   * Active/désactive la caméra locale directement (pas un toggle) — symétrique
   * de mute()/unmute() pour le micro. Utilisé à l'entrée en zone pour
   * appliquer le choix fait dans LobbyPage (connectToZone() ne touche jamais
   * à la caméra, contrairement au micro qui a un warm-up puis mute).
   */
  async setCameraEnabled(enabled: boolean): Promise<void> {
    await this.localAvRoom?.localParticipant.setCameraEnabled(enabled);
  }

  /**
   * Toggle micro — appelé par le bouton HUD. Retourne l'état résultant (true = muté).
   * Agit sur localAvRoom : meetingRoomRoom pendant une réunion meeting-room active,
   * zoneRoom sinon (sans quoi le bouton HUD contrôlerait une room qui ne publie plus rien).
   */
  async toggleMic(): Promise<boolean> {
    // Bloqué si muté par admin
    if (this.isMutedByAdmin()) {
      console.log('[LiveKitService] toggleMic bloqué — muté par admin');
      return true;
    }
    const room = this.localAvRoom;
    const lp = room?.localParticipant;
    if (!room || !lp) return true;
    const willEnable = !lp.isMicrophoneEnabled;
    try {
      // Fix #11 (2026-08-22) : sérialise ce clic contre toute application AV
      // automatique en vol sur la MÊME room (switchToMeetingRoom/
      // switchBackToZoneRoom, via applyAvState) — sans withRoomLock, les deux
      // set*Enabled couraient en parallèle sur le même localParticipant,
      // résultat dépendant de l'ordre de résolution réel plutôt que de la
      // dernière intention utilisateur (confirmé par simulation). Relit l'état
      // UNE FOIS le verrou obtenu : si une transition automatique a déjà
      // amené le mic dans l'état visé pendant l'attente, rien à refaire.
      await this.withRoomLock(room, async () => {
        if (lp.isMicrophoneEnabled === willEnable) return;
        await lp.setMicrophoneEnabled(willEnable);
      });
    } catch (e: unknown) {
      const err = e as Error;
      console.warn(`[LiveKitService] toggleMic error (${err.name}):`, err.message);
      // Sans ce toast, un refus de permission navigateur est totalement silencieux
      // pour l'utilisateur : le bouton reste rouge sans aucune explication.
      if (willEnable) EventBus.emit('ui:toast', { message: mediaErrorMessage(err, 'microphone'), tone: 'danger' });
      return true;
    }
    return !willEnable; // retourne isLocalMuted
  }

  /**
   * Occupé / Focus : ne plus entendre ni voir personne (true), ou revenir aux
   * souscriptions normales (false). Agit sur la room de zone (podium gardé)
   * et sur la salle de réunion en cours.
   */
  setDeafened(deafened: boolean): void {
    if (this.deafened === deafened) return;
    this.deafened = deafened;
    if (this.zoneRoom) this.updateSubscriptions(this.currentNeighbors, this.currentBubbleId, this.playerBubbleIds);
    this.meetingRoomRoom?.remoteParticipants.forEach((participant) => {
      participant.trackPublications.forEach((pub) => {
        if ((pub.kind === Track.Kind.Audio || pub.kind === Track.Kind.Video) && pub.isSubscribed === deafened) {
          pub.setSubscribed(!deafened);
        }
      });
    });
  }

  /**
   * Met le micro et la caméra dans l'état voulu (statuts Absent, Occupé,
   * Focus, et retour à Disponible). Même verrou que les boutons du HUD ; le
   * micro reste coupé tant qu'un admin le verrouille. Renvoie l'état obtenu
   * (un échec d'activation, permission retirée par exemple, laisse coupé).
   */
  async setLocalMedia(target: { mic: boolean; camera: boolean }): Promise<{ mic: boolean; camera: boolean }> {
    const room = this.localAvRoom;
    const lp = room?.localParticipant;
    if (!room || !lp) return { mic: false, camera: false };
    const mic = target.mic && !this.isMutedByAdmin();
    await this.withRoomLock(room, async () => {
      try {
        if (lp.isMicrophoneEnabled !== mic) await lp.setMicrophoneEnabled(mic);
      } catch (err) {
        console.warn('[LiveKitService] setLocalMedia micro:', err);
      }
      try {
        if (lp.isCameraEnabled !== target.camera) await lp.setCameraEnabled(target.camera);
      } catch (err) {
        console.warn('[LiveKitService] setLocalMedia caméra:', err);
      }
    });
    return { mic: lp.isMicrophoneEnabled, camera: lp.isCameraEnabled };
  }

  /** Vérifie si le micro est verrouillé par un admin. */
  isMutedByAdmin(): boolean {
    return Date.now() < this._mutedByAdminUntil;
  }

  /** Temps restant du mute admin en ms. 0 si pas de mute actif. */
  getAdminMuteRemainingMs(): number {
    return Math.max(0, this._mutedByAdminUntil - Date.now());
  }

  /**
   * Verrouille le micro pour une durée fixe (appelé quand moderation:muted reçu).
   * Le user ne peut pas réactiver son micro pendant cette durée.
   */
  setAdminMuteLock(durationMs: number): void {
    this._mutedByAdminUntil = Date.now() + durationMs;
    // Annuler un éventuel timeout précédent
    if (this._adminMuteTimeout) clearTimeout(this._adminMuteTimeout);
    // Auto-release après la durée
    this._adminMuteTimeout = setTimeout(() => {
      this._mutedByAdminUntil = 0;
      this._adminMuteTimeout = null;
      console.log('[LiveKitService] admin mute lock expiré');
    }, durationMs);
  }

  /** Lève le verrou du micro avant sa fin (l'admin a rendu la parole). */
  clearAdminMuteLock(): void {
    if (this._adminMuteTimeout) clearTimeout(this._adminMuteTimeout);
    this._adminMuteTimeout = null;
    this._mutedByAdminUntil = 0;
  }

  /** Toggle caméra — appelé par le bouton HUD. Retourne l'état résultant (true = off). */
  async toggleCamera(): Promise<boolean> {
    const room = this.localAvRoom;
    const lp = room?.localParticipant;
    if (!room || !lp) return true;
    const willEnable = !lp.isCameraEnabled;
    try {
      // Fix #11 — voir le commentaire équivalent dans toggleMic.
      await this.withRoomLock(room, async () => {
        if (lp.isCameraEnabled === willEnable) return;
        await lp.setCameraEnabled(willEnable);
      });
    } catch (e: unknown) {
      const err = e as Error;
      console.warn(`[LiveKitService] toggleCamera error (${err.name}):`, err.message);
      if (willEnable) EventBus.emit('ui:toast', { message: mediaErrorMessage(err, 'caméra'), tone: 'danger' });
      return true;
    }
    return !willEnable; // retourne isLocalCameraOff
  }

  /** Toggle partage d'écran. Retourne true si le partage est actif après l'appel. */
  async toggleScreenShare(): Promise<boolean> {
    const room = this.localAvRoom;
    const lp = room?.localParticipant;
    if (!room || !lp) return false;
    const willEnable = !lp.isScreenShareEnabled;
    // Fix #11 — voir le commentaire équivalent dans toggleMic. La direction du
    // toggle (démarrer/arrêter) est décidée avant d'obtenir le verrou ; l'état
    // réellement appliqué est revérifié une fois le verrou obtenu.
    await this.withRoomLock(room, async () => {
      if (lp.isScreenShareEnabled === willEnable) return;
      if (willEnable) {
        await lp.setScreenShareEnabled(true);
        // Log les tracks publiées après activation
        lp.trackPublications.forEach((pub) => {
          if (pub.source === Track.Source.ScreenShare) {
            console.log('[LiveKit:screen] track publiée', pub.trackSid, 'source:', pub.source, 'kind:', pub.kind);
          }
        });
      } else {
        console.log('[LiveKit:screen] arrêt du partage d\'écran');
        await lp.setScreenShareEnabled(false);
      }
    });
    return willEnable; // retourne l'état de partage résultant
  }

  // ── Broadcast (pattern Ding-Dong) ────────────────────────

  /**
   * Connexion à la room broadcast — appelé quand broadcast:started est reçu.
   * Pendant ce temps, le jingle joue côté UI (2s minimum).
   * La connexion est établie en parallèle, puis on attend room.connected.
   */
  async connectToBroadcast(token: string, url: string): Promise<void> {
    if (this.broadcastRoom) return; // déjà connecté

    this.broadcastRoom = new Room();

    // room.connect() résout sur RoomEvent.Connected — pas de setTimeout hardcodé
    await this.broadcastRoom.connect(url, token, { autoSubscribe: true });

    // Notifier le UI que le broadcast est prêt à être écouté
    EventBus.emit('broadcast:ready', {});
  }

  async disconnectFromBroadcast(): Promise<void> {
    if (this.broadcastRoom) {
      await this.broadcastRoom.disconnect();
      this.broadcastRoom = null;
    }
  }

  // ── Meeting-room (room LiveKit dédiée à une bulle meeting-room) ──

  /**
   * Connexion à la room LiveKit dédiée à une bulle meeting-room — sur le
   * modèle de connectToBroadcast/disconnectFromBroadcast.
   *
   * autoSubscribe: true (contrairement à zoneRoom) : dans cette room, tout
   * le monde connecté fait par définition partie de la réunion — pas de
   * filtrage par souscription à faire. C'est justement pour ça qu'une room
   * séparée est nécessaire : Room Composite Egress (Task 15) enregistre tout
   * le monde connecté à la room, indépendamment des souscriptions.
   *
   * Ne gère pas mic/caméra elle-même — c'est switchToMeetingRoom qui décide
   * de l'état AV à reproduire depuis zoneRoom.
   *
   * Amendement Task 16 : `gen` identifie la transition appelante. Le garde
   * "déjà connecté" reste présence-only pour la génération COURANTE (no-op
   * légitime, comportement inchangé) — mais s'il trouve une room appartenant à
   * une génération PÉRIMÉE (orpheline, jamais nettoyée par son propriétaire
   * d'origine — ex: switchBackToZoneRoom abandonné en cours de route après
   * être devenu périmé), il la déconnecte avant de connecter la cible fraîche
   * au lieu de no-op à tort dessus (c'était le mécanisme du bug résiduel :
   * un connectToMeetingRoom pouvait no-op silencieusement sur une cible déjà
   * périmée). Fraîcheur revérifiée une seconde fois après l'aller-retour
   * réseau de connexion, avant de committer `this.meetingRoomRoom` — si
   * périmée à ce moment-là, la room tout juste connectée est immédiatement
   * déconnectée plutôt que committée.
   */
  async connectToMeetingRoom(token: string, url: string, gen: number): Promise<void> {
    if (this.isStaleGeneration(gen)) {
      console.log(`[LiveKitService:meeting] connectToMeetingRoom(gen=${gen}) périmée avant connexion (courante=${this.transitionGeneration}) — abandon`);
      return;
    }

    if (this.meetingRoomRoom) {
      if (this.meetingRoomOwnerGeneration === gen) return; // déjà connecté pour CETTE transition précise
      const staleOwner = this.meetingRoomOwnerGeneration;
      const stale = this.meetingRoomRoom;
      this.meetingRoomRoom = null;
      this.meetingRoomOwnerGeneration = null;
      console.log(`[LiveKitService:meeting] room meeting orpheline détectée (génération propriétaire=${staleOwner ?? 'inconnue'}, courante=${gen}) — déconnexion avant de connecter`);
      await stale.disconnect().catch((err: unknown) => {
        console.warn('[LiveKitService:meeting] déconnexion room orpheline échouée:', err);
      });
    }

    const room = new Room(this.buildRoomOptions());
    this.setupMeetingRoomListeners(room);

    // room.connect() résout sur RoomEvent.Connected — pas de setTimeout hardcodé.
    // Une exception ici remonte telle quelle à switchToMeetingRoom (rien à
    // committer, this.meetingRoomRoom n'a jamais été touché dans ce chemin).
    await room.connect(url, token, { autoSubscribe: true });

    if (this.isStaleGeneration(gen)) {
      // Une transition plus récente a émergé pendant l'aller-retour réseau de
      // connexion : ne PAS committer cette room comme meetingRoomRoom (cible
      // périmée) — la déconnecter immédiatement pour ne pas laisser de connexion
      // LiveKit fantôme consommer de la bande passante / être enregistrée par
      // Room Composite Egress pour rien.
      console.log(`[LiveKitService:meeting] connectToMeetingRoom(gen=${gen}) périmée après connexion (courante=${this.transitionGeneration}) — déconnexion immédiate, abandon`);
      await room.disconnect().catch((err: unknown) => {
        console.warn('[LiveKitService:meeting] déconnexion room périmée (post-connect) échouée:', err);
      });
      return;
    }

    this.meetingRoomRoom = room;
    this.meetingRoomOwnerGeneration = gen;
  }

  async disconnectFromMeetingRoom(): Promise<void> {
    if (this.meetingRoomRoom) {
      await this.meetingRoomRoom.disconnect();
      this.meetingRoomRoom = null;
      this.meetingRoomOwnerGeneration = null;
    }
  }

  /**
   * Écoute minimale sur meetingRoomRoom : attache/détache les tracks audio
   * distantes à des éléments <audio> DOM (même mécanisme que
   * setupZoneRoomListeners) — indispensable pour que les participants
   * s'entendent réellement, autoSubscribe: true ne suffit pas à jouer le
   * son. Ne réutilise PAS setupZoneRoomListeners telle quelle : celle-ci
   * émet 'livekit:disconnected'/'livekit:speakers-changed' avec une
   * sémantique "zone" (ex: GameBridge met liveKitConnected à false sur
   * livekit:disconnected) qui serait incorrecte si émise pour cette room.
   */
  private setupMeetingRoomListeners(room: Room): void {
    room.on(RoomEvent.Connected, () => {
      void room.startAudio();
    });
    room.on(RoomEvent.LocalTrackPublished, (pub) => { if (pub.source === Track.Source.Camera) void syncVideoBackground(room); });
    room.on(RoomEvent.AudioPlaybackStatusChanged, () => this.onAudioPlaybackChanged(room));

    room.on(RoomEvent.TrackSubscribed, (track: RemoteTrack, pub: RemoteTrackPublication, participant: RemoteParticipant) => {
      // autoSubscribe : en Occupé/Focus, on refuse aussitôt ce qui arrive.
      if (this.deafened) {
        pub.setSubscribed(false);
        return;
      }
      if (pub.kind === Track.Kind.Audio) {
        const el = track.attach();
        el.dataset['livekitParticipant'] = participant.identity;
        el.dataset['livekitRoom'] = 'meeting';
        document.body.appendChild(el);
      }
    });

    room.on(RoomEvent.TrackUnsubscribed, (track: RemoteTrack) => {
      track.detach().forEach((el) => el.remove());
    });

    // livekit:speakers-changed n'a pas la sémantique "zone" de
    // livekit:disconnected (voir docstring de méthode) — GameBridge s'en
    // sert juste pour alimenter gameStore.activeSpeakers, sans effet de
    // bord sur liveKitConnected. Sans ce handler, l'indicateur de prise de
    // parole restait figé sur les speakers de zoneRoom (vide, AV coupée)
    // pendant toute une réunion (trouvé en review, 2026-08-23, même finding
    // que VideoTiles bloqué sur zoneRoom).
    room.on(RoomEvent.ActiveSpeakersChanged, (speakers) => {
      EventBus.emit('livekit:speakers-changed', {
        speakerUserIds: speakers.map((s) => s.identity),
      });
    });

    // Important trouvé en review (2026-08-23) : aucune gestion de
    // déconnexion inattendue sur cette room (crash du process egress/serveur
    // LiveKit, coupure réseau, ou le token à 2h — voir MediaService.ts —
    // expirant en pleine réunion). Sans ce handler, l'utilisateur restait
    // bloqué avec inMeetingRoom=true, zoneRoom toujours coupée des deux
    // côtés (AV muette), sans aucun signal ni recours avant de sortir
    // physiquement de la bulle. setupZoneRoomListeners émet déjà
    // livekit:disconnected pour son équivalent zone — cette room dédiée
    // n'avait jamais eu son pendant.
    room.on(RoomEvent.Disconnected, () => {
      // Une déconnexion PROVOQUÉE par switchBackToZoneRoom (sortie normale
      // de bulle) a déjà mis inMeetingRoom à false de façon SYNCHRONE, tout
      // au début de cette méthode, bien avant que roomToLeave.disconnect()
      // ne déclenche cet event de façon asynchrone — donc si inMeetingRoom
      // est encore true ici, c'est que PERSONNE n'a demandé cette
      // déconnexion : un vrai repli est nécessaire, pas un double-traitement
      // d'une sortie déjà en cours.
      if (!this.inMeetingRoom || room !== this.meetingRoomRoom) return;

      console.warn('[LiveKitService:meeting] meetingRoomRoom déconnectée de façon inattendue — repli vers zoneRoom');
      EventBus.emit('ui:toast', { message: 'Connexion à la réunion perdue - retour à la zone.', tone: 'danger' });

      const gen = ++this.transitionGeneration;
      // Sans ce reset, le gating de switchBackToZoneRoom (shouldRepublishToZone)
      // croirait à tort qu'une AUTRE meeting-room suit dans une chaîne A→B —
      // ici il n'y a pas de chaîne, juste une perte de connexion à traiter
      // comme une sortie complète vers la zone ouverte.
      this.latestBubbleMeetingTarget = null;
      void this.switchBackToZoneRoom(this.currentBubbleId, gen);
    });
  }

  // ── Logique de souscription ───────────────────────────────

  /**
   * Détermine si on doit souscrire aux tracks d'un participant.
   *
   * Règles (par ordre de priorité) :
   *   1. Joueur local en bulle fixe (bubbleId !== null)
   *      → souscrire UNIQUEMENT aux membres de la même bulle.
   *      La proximité est ignorée : l'isolement bulle prime.
   *
   *   2. Joueur local hors bulle (bubbleId === null)
   *      → souscrire si userId ∈ (currentNeighbors ∪ proximityUserIds)
   *        ET que l'autre joueur est lui-même hors bulle.
   *        Union des deux sources : Phaser (player:nearby) + Colyseus (proximity:changed).
   */
  private shouldSubscribeTo(userId: string, log = true): boolean {
    if (this.deafened) return false;
    const theirBubbleId = this.playerBubbleIds.get(userId) ?? null;

    if (this.currentBubbleId !== null) {
      // Joueur local en bulle fixe → seuls les membres de la même bulle
      const result = theirBubbleId === this.currentBubbleId;
      if (log) console.log(`[LiveKitService:shouldSub] ${userId} → ${result} (local in bubble "${this.currentBubbleId}", their="${theirBubbleId}")`);
      return result;
    }

    // Joueur local hors bulle → union proximité Phaser + proximité Colyseus
    const inPhaser   = this.currentNeighbors.has(userId);
    const inColyseus = this.proximityUserIds.has(userId);
    const isNearby   = inPhaser || inColyseus;
    const result     = isNearby && theirBubbleId === null;
    if (log) console.log(`[LiveKitService:shouldSub] ${userId} → ${result} (phaser=${inPhaser} colyseus=${inColyseus} theirBubble=${theirBubbleId ?? 'none'})`);
    return result;
  }

  // ── Event listeners ───────────────────────────────────────

  private setupZoneRoomListeners(room: Room): void {
    room.on(RoomEvent.Connected, () => {
      console.log(`[LiveKitService] ✓ room connected — localIdentity=${room.localParticipant.identity} remoteParticipants=${room.remoteParticipants.size}`);
      // L'AudioContext des navigateurs est suspendu jusqu'à la première interaction
      // utilisateur (politique autoplay). room.startAudio() le résume si nécessaire.
      // On l'appelle ici (post-connexion) ET sur le premier clic pour couvrir les
      // deux cas : connexion avant ou après l'interaction.
      void room.startAudio();
    });
    room.on(RoomEvent.AudioPlaybackStatusChanged, () => this.onAudioPlaybackChanged(room));
    room.on(RoomEvent.LocalTrackPublished, (pub) => { if (pub.source === Track.Source.Camera) void syncVideoBackground(room); });

    room.on(RoomEvent.ParticipantConnected, (participant: RemoteParticipant) => {
      console.log(`[LiveKitService] ParticipantConnected identity=${participant.identity} tracks=${participant.trackPublications.size}`);
      const shouldSubscribe = this.shouldSubscribeTo(participant.identity);
      participant.trackPublications.forEach((pub) => {
        if ((pub.kind === Track.Kind.Audio || pub.kind === Track.Kind.Video) && pub.isSubscribed !== shouldSubscribe) {
          console.log(`[LiveKitService] ✓ setSubscribed(${shouldSubscribe}) on ParticipantConnected → ${participant.identity} [${pub.kind}]`);
          pub.setSubscribed(shouldSubscribe);
        }
      });
    });

    room.on(RoomEvent.TrackPublished, (pub: RemoteTrackPublication, participant: RemoteParticipant) => {
      console.log(`[LiveKitService] TrackPublished kind=${pub.kind} source=${pub.source} identity=${participant.identity} isSubscribed=${pub.isSubscribed}`);
      if (pub.source === Track.Source.ScreenShare) {
        console.log('[LiveKit:screen] TrackPublished screenShare de', participant.identity, 'kind:', pub.kind);
      }
      if (pub.kind === Track.Kind.Audio || pub.kind === Track.Kind.Video) {
        const shouldSubscribe = this.shouldSubscribeTo(participant.identity);
        console.log(`[LiveKitService] TrackPublished ${pub.kind} source=${pub.source} → shouldSubscribe=${shouldSubscribe}`);
        if (pub.isSubscribed !== shouldSubscribe) pub.setSubscribed(shouldSubscribe);
      }
    });

    room.on(RoomEvent.TrackSubscribed, (track: RemoteTrack, pub: RemoteTrackPublication, participant: RemoteParticipant) => {
      console.log('[LiveKit] TrackSubscribed', pub.kind, 'source:', pub.source, 'de', participant.identity, 'attached:', track.attachedElements.length);
      if (pub.source === Track.Source.ScreenShare) {
        console.log('[LiveKit:screen] TrackSubscribed screenShare de', participant.identity, 'kind:', pub.kind, 'sid:', pub.trackSid);
      }
      if (pub.kind === Track.Kind.Audio) {
        // Attacher la track à un élément <audio> injecté dans le DOM.
        // Sans cette étape, isSubscribed=true mais aucun son n'est émis :
        // la WebRTC track existe mais n'est connectée à aucun sink audio.
        const el = track.attach();
        el.dataset['livekitParticipant'] = participant.identity;
        document.body.appendChild(el);
        console.log('[LiveKit] ✓ audio element attaché et injecté pour', participant.identity);
      }
    });

    room.on(RoomEvent.TrackUnsubscribed, (track: RemoteTrack, _pub: RemoteTrackPublication, participant: RemoteParticipant) => {
      console.log('[LiveKit] TrackUnsubscribed', participant.identity, '— détachement et suppression élément audio');
      track.detach().forEach((el) => el.remove());
    });

    room.on(RoomEvent.Disconnected, () => {
      EventBus.emit('livekit:disconnected', { roomType: 'zone' });
    });

    room.on(RoomEvent.ActiveSpeakersChanged, (speakers) => {
      EventBus.emit('livekit:speakers-changed', {
        speakerUserIds: speakers.map((s) => s.identity),
      });
    });
  }
}
